import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

type QueryStateOptions<T> = {
  defaultValue: T;
  serialize?: (value: T) => string;
  deserialize?: (value: string) => T;
};

export function useQueryState<T extends string | number>(
  key: string,
  options: QueryStateOptions<T>
): [T, (value: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const serialize = useMemo(
    () => options.serialize || ((value: T) => String(value)),
    [options.serialize]
  );
  const deserialize = useMemo(
    () => options.deserialize || ((value: string) => value as unknown as T),
    [options.deserialize]
  );

  const value = useMemo(() => {
    const paramValue = searchParams.get(key);
    if (paramValue === null) {
      return options.defaultValue;
    }
    return deserialize(paramValue);
  }, [searchParams, key, options.defaultValue, deserialize]);

  const setValue = useCallback(
    (newValue: T) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(key, serialize(newValue));
          return next;
        },
        { replace: true }
      );
    },
    [key, serialize, setSearchParams]
  );

  return [value, setValue];
}

export function useMultiQueryState<T extends Record<string, string | number>>(
  defaults: T
): [T, (updates: Partial<T>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const prevValuesRef = useRef<T | null>(null);

  const values = useMemo(() => {
    const result: Record<string, string | number> = { ...defaults };
    Object.keys(defaults).forEach((key) => {
      const paramValue = searchParams.get(key);
      if (paramValue !== null) {
        const defaultValue = defaults[key];
        if (typeof defaultValue === 'number') {
          result[key] = Number(paramValue);
        } else {
          result[key] = paramValue;
        }
      }
    });

    // Return previous reference if values haven't actually changed
    if (prevValuesRef.current) {
      const hasChanged = Object.keys(result).some(
        (key) => result[key] !== prevValuesRef.current![key]
      );
      if (!hasChanged) {
        return prevValuesRef.current;
      }
    }

    prevValuesRef.current = result as T;
    return result as T;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setValues = useCallback(
    (updates: Partial<T>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            next.set(key, String(value));
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return [values, setValues];
}
